<template>
  <UpdateProgressOverlay
    :open="updateInProgress"
    :phase="overlayPhase"
    :slow="updateSlow"
    :from-version="APP_VERSION"
    :to-version="updateTarget?.version ?? null"
    @reload="onReload"
  />
</template>

<script setup lang="ts">
/**
 * Host for the update wall and for the toast that confirms an update after the
 * reload.
 *
 * Two jobs, both app-wide, which is why it is mounted in App.vue next to the two
 * update prompts (UpdatePromptHost's banner, UpdateRequiredHost's modal):
 *  1. While an update session runs (`src/pwa/update-session.ts`), render the
 *     "Updating Syntry…" wall over everything — including the standalone pages
 *     and the public routes, where the watchdog modal is also allowed.
 *  2. On the FIRST mount of a document — i.e. right after the reload an update
 *     ends in — read the handoff the previous page left and say what happened:
 *     "Update complete" (with the success cue) when the app really changed —
 *     its version/build, its entry module, or an update worker took control
 *     (`update-session.ts`, "The handoff"); a warning, silent, when the user
 *     asked for an update and the reload came back on the same build with no
 *     sign of a change (it did not land — the prompt will return); nothing for
 *     a passive reload that changed nothing.
 *
 * The wall has no close: its only control is Reload, offered once the session
 * has stalled (`update-session.ts` explains why there is no "give the app back"
 * state after anything destructive).
 */
import { computed, onMounted } from 'vue';
import UpdateProgressOverlay, {
  type UpdateOverlayPhase,
} from '@/components/layout/UpdateProgressOverlay.vue';
import { useToast } from '@/composables/useToast';
import { reloadPage } from '@/pwa/reload';
import { primeNotificationSound } from '@/services/notification-sound';
import {
  consumeUpdateHandoff,
  markRestarting,
  updateInProgress,
  updatePhase,
  updateSlow,
  updateTarget,
} from '@/pwa/update-session';
import { APP_VERSION } from '@/utils/app-info';

/** Longer than the success default (3 s): it lands while the app is still painting. */
const UPDATED_TOAST_MS = 4_500;

const toaster = useToast();

/** `idle` never reaches the overlay — it is closed then — so map it to anything valid. */
const overlayPhase = computed<UpdateOverlayPhase>(() =>
  updatePhase.value === 'idle' ? 'updating' : updatePhase.value,
);

/**
 * The stalled wall's Reload. Straight to the page reload, NOT through
 * register.ts's single-flight guard: that guard is exactly what already fired
 * for the reload that never landed.
 */
function onReload(): void {
  markRestarting();
  reloadPage();
}

onMounted(async () => {
  const outcome = consumeUpdateHandoff();
  if (!outcome) return;
  try {
    if (outcome.kind === 'updated') {
      // A child mounts before its parent, so this runs BEFORE App.vue's own
      // onMounted — i.e. before `notificationsDevice.prepare()` creates the
      // sound clips. Create them here (idempotent) or the cue has nothing to
      // play. The clip is still subject to the autoplay policy; a refused
      // play() is swallowed by the service, never surfaced.
      primeNotificationSound();
      await toaster.success(`Syntry is now updated to the latest version (v${APP_VERSION}).`, {
        title: 'Update complete',
        duration: UPDATED_TOAST_MS,
        sound: true,
      });
    } else if (outcome.explicit) {
      await toaster.warning("Syntry couldn't finish updating. You'll be offered the update again shortly.");
    }
  } catch {
    /* a missed toast must never break boot */
  }
});
</script>
